import { SearchParameters } from '../../../../../ListFilter/Types';
import { Resource, ResourceWithMetadata } from '../../../../../Resource/Types';
import { Error } from '../../../../../ErrorHandling';
import { PaginationMetadata } from '@execonline-inc/resource';
import { Maybe } from 'maybeasy';
import { InvitationStatus } from './InvitationStore/Types';
import { AlreadyTranslatedText } from '@execonline-inc/translations';

interface BaseState {
  page: number;
  searchParameters: SearchParameters;
  invitationsResource: InvitationsResource;
}

export interface Ready extends BaseState {
  kind: 'ready';
}

export interface UpdatingQuery extends BaseState {
  kind: 'updating-query';
}

export interface UpdatingSearchParameters extends BaseState {
  kind: 'updating-search-parameters';
}

export interface Sorting extends BaseState {
  kind: 'sorting';
}
interface Initializing {
  kind: 'initializing';
  searchParameters: SearchParameters;
}
export interface Loading {
  kind: 'loading';
  page: number;
  searchParameters: SearchParameters;
}

export interface LoadingMore extends BaseState {
  kind: 'loading-more';
  page: number;
}

export const initializing = (searchParameters: SearchParameters): Initializing => {
  return {
    kind: 'initializing',
    searchParameters,
  };
};

export const loading = (
  state: Initializing | Sorting | Ready | UpdatingSearchParameters,
  page: number,
): Loading => {
  const { kind, ...previous } = state;
  return {
    kind: 'loading',
    ...previous,
    page,
  };
};

export const loadingMore = (state: Ready, page: number): LoadingMore => {
  const { kind, ...previous } = state;
  return {
    kind: 'loading-more',
    ...previous,
    page,
  };
};

export const ready = (
  state: Loading | LoadingMore | UpdatingQuery | UpdatingSearchParameters | Ready,
  invitationsResource: InvitationsResource,
): Ready => {
  const { kind, ...previous } = state;
  return { kind: 'ready', ...previous, invitationsResource };
};

export const updatingSearchParameters = (
  searchParameters: SearchParameters,
  state: Ready,
): UpdatingSearchParameters => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-search-parameters',
    ...previous,
    searchParameters,
  };
};

export const updatingQuery = (searchParameters: SearchParameters, state: Ready): UpdatingQuery => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-query',
    ...previous,
    searchParameters,
  };
};

export const sorting = (searchParameters: SearchParameters, state: Ready): Sorting => {
  const { kind, ...previous } = state;

  return {
    kind: 'sorting',
    ...previous,
    searchParameters: searchParameters,
  };
};

export type InvitationType =
  | 'direct-enrollment'
  | 'open-enrollment'
  | 'shared-open-enrollment'
  | 'program-family-shared-open-enrollment'
  | 'coach-recommended'
  | 'preview-only'
  | 'group-membership';

export type RegistrationType = 'auditor' | 'student';

export interface CourseRegistration {
  studentStatus: AlreadyTranslatedText;
  enrolledOn: Maybe<Date>;
  publicName: AlreadyTranslatedText;
}

export type CourseRegistrationResource = Resource<CourseRegistration>;

// What happened to the invitation email (DVLP-8056). Someone added without an
// email is "not-emailed"; the time they were added is never shown as an email.
// communicationName names the email the time belongs to: the one sent, or the
// one coming.
export interface SentInvitationEmail {
  kind: 'sent';
  at: Date;
  communicationName: Maybe<string>;
}

export interface ScheduledInvitationEmail {
  kind: 'scheduled';
  at: Date;
  communicationName: Maybe<string>;
}

export interface NotEmailed {
  kind: 'not-emailed';
}

export type InvitationEmail = SentInvitationEmail | ScheduledInvitationEmail | NotEmailed;

export type Invitation = {
  id: number;
  name: string;
  email: string;
  registrationType: RegistrationType;
  totalEnrollments: number;
  totalInvitations: number;
  invitationEmail: InvitationEmail;
  invitationAcceptedDate: Maybe<Date>;
  initials: Maybe<string>;
  invitationStatus: InvitationStatus;
  courseRegistration: Array<CourseRegistrationResource>;
};

export type InvitationResource = Resource<Invitation>;

export type Invitations = {
  totalInvitations: number;
  invitations: InvitationResource[];
};

export type InvitationsMetadata = PaginationMetadata;

export type InvitationsResource = ResourceWithMetadata<Invitations, InvitationsMetadata>;

export type State =
  | Initializing
  | Loading
  | LoadingMore
  | Ready
  | UpdatingQuery
  | UpdatingSearchParameters
  | Sorting
  | Error;

export type SortableColumn =
  | 'name'
  | 'email'
  | 'invitation_type'
  | 'total_enrollments'
  | 'access_status'
  | 'total_invitations'
  | 'send_date';

export type FilterKey =
  | 'invitation_type'
  | 'invitation_status'
  | 'user_groups'
  | 'program_family'
  | 'invitation_accepted_date'
  | 'send_date';
